import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useAuth0 } from '../../components/auth/DevAuth0Provider';
import { fetchActivities } from '../../api/activityApi';

// Helper function to highlight search text
const highlightText = (text, searchTerm) => {
  if (!searchTerm || !text) return text;
  
  const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <span key={index} className="bg-yellow-200 text-yellow-900 px-1 rounded">
        {part}
      </span>
    ) : part
  );
};

// Helper functions for activity display
const getActivityIcon = (type) => {
  switch (type) {
    // User Management
    case 'user_login': return 'mdi:login';
    case 'user_logout': return 'mdi:logout';
    case 'user_created': return 'mdi:account-plus';
    case 'user_updated': return 'mdi:account-edit';
    case 'user_deleted': return 'mdi:account-remove';
    case 'user_role_assigned': return 'mdi:account-key';
    case 'user_role_removed': return 'mdi:account-key-outline';
    
    // Customer Management
    case 'customer_created': return 'mdi:domain-plus';
    case 'customer_updated': return 'mdi:domain-edit';
    case 'customer_deleted': return 'mdi:domain-remove';
    case 'organization_member_added': return 'mdi:account-group-outline';
    case 'organization_member_removed': return 'mdi:account-group';
    
    // System Activities
    case 'call_feedback_submitted': return 'mdi:message-text';
    case 'ticket_created': return 'mdi:ticket-outline';
    case 'ticket_updated': return 'mdi:ticket';
    case 'alert_created': return 'mdi:alert-outline';
    case 'zoho_auth_connected': return 'mdi:link';
    case 'zoho_auth_disconnected': return 'mdi:link-off';
    
    // Admin Activities
    case 'admin_security_setting_changed': return 'mdi:security';
    case 'admin_bootstrap_customer_created': return 'mdi:bootstrap';
    
    default: return 'mdi:information';
  }
};

const getActivityColor = (type) => {
  switch (type) {
    // User Management - Blue tones
    case 'user_login': return 'text-blue-400';
    case 'user_logout': return 'text-blue-300';
    case 'user_created': return 'text-blue-500';
    case 'user_updated': return 'text-blue-600';
    case 'user_deleted': return 'text-error';
    case 'user_role_assigned': return 'text-purple-400';
    case 'user_role_removed': return 'text-purple-300';
    
    // Customer Management - Green tones
    case 'customer_created': return 'text-green-400';
    case 'customer_updated': return 'text-green-500';
    case 'customer_deleted': return 'text-error';
    case 'organization_member_added': return 'text-emerald-400';
    case 'organization_member_removed': return 'text-emerald-300';
    
    // System Activities - Yellow/Orange tones
    case 'call_feedback_submitted': return 'text-yellow-400';
    case 'ticket_created': return 'text-orange-400';
    case 'ticket_updated': return 'text-orange-500';
    case 'alert_created': return 'text-error';
    case 'zoho_auth_connected': return 'text-orange-400';
    case 'zoho_auth_disconnected': return 'text-orange-300';
    
    // Admin Activities - Cyan/Teal tones
    case 'admin_security_setting_changed': return 'text-cyan-400';
    case 'admin_bootstrap_customer_created': return 'text-teal-400';
    
    default: return 'text-gray-400';
  }
};

const formatTimestamp = (timestamp) => {
  const now = new Date();
  const activityDate = new Date(timestamp);
  const diff = now - activityDate;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else {
    return 'Less than an hour ago';
  }
};

const formatUserName = (username) => {
  if (!username) return '';
  
  // Check if it's in firstname.lastname format
  if (username.includes('.')) {
    return username
      .split('.')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }
  
  // If not in expected format, just return as is with proper capitalization
  return username.charAt(0).toUpperCase() + username.slice(1).toLowerCase();
};

const AdminActivityTab = ({ searchQuery }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Load activities from API
  useEffect(() => {
    const loadActivities = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const filters = {
          limit: 50,
          offset: 0
        };
        
        // Add search filter if there's a query
        if (searchQuery && searchQuery.trim() !== '') {
          filters.search = searchQuery.trim();
        }
        
        const response = await fetchActivities(filters, getAccessTokenSilently);
        
        const activitiesList = response.activities || [];
        setActivities(activitiesList);
        setTotalCount(response.totalCount || 0);
        
        // Note: Count will be updated in separate useEffect after filtering
      } catch (error) {
        console.error("Failed to load activities:", error);
        setError("Failed to load activities. Please try again later.");
        setActivities([]);
        setTotalCount(0);
        
        // Note: Count will be updated in separate useEffect after filtering
      } finally {
        setLoading(false);
      }
    };
    
    loadActivities();
  }, [searchQuery, getAccessTokenSilently]);

  // Filter activities based on search query if API doesn't support search
  const filteredActivities = searchQuery.trim() === '' 
    ? activities 
    : activities.filter(activity => {
        const searchLower = searchQuery.toLowerCase();
        const searchableFields = [
          activity.description,
          activity.user,
          activity.customerName,
          activity.email,
          activity.role,
          activity.type
        ].filter(Boolean); // Remove undefined/null values
        
        return searchableFields.some(field => 
          field.toString().toLowerCase().includes(searchLower)
        );
      });
      
  // Update the count in the parent component whenever filtered results change
  useEffect(() => {
    if (window.updateAdminCount && !loading) {
      // Only update count when not loading to avoid race conditions
      window.updateAdminCount('activitiesCount', filteredActivities.length);
    }
  }, [filteredActivities.length, loading]);

  return (
    <div className="flex flex-1 gap-2 w-full min-h-0">
      <div className="flex flex-col w-full transition-all duration-300 ease-out">
        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto scrollable">
          {loading ? (
            <div className="text-center py-8">
              <Icon icon="eos-icons:loading" className="animate-spin text-4xl text-accent mb-4" />
              <p className="text-accent">Loading activities...</p>
            </div>
          ) : error ? (
            <div className="text-center mb-4 p-4">
              <p className="text-yellow-500">⚠️ {error}</p>
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full bg-bgSecondary card-container rounded-lg p-8">
              <Icon icon="hugeicons:activity-04" className="text-6xl text-gray-400 mb-4" />
              <h3 className="text-xl text-font-primary mb-2">No activities found</h3>
              <p className="text-font-secondary text-center">
                {searchQuery.trim() !== '' 
                  ? "No activities found matching your search criteria." 
                  : "No recent activity to display."}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View (xl and above) */}
              <table className="w-full table-auto text-left border-collapse text-sm hidden xl:table">
                <thead>
                  <tr className="text-font-primary h-[48px] bg-bgSecondary card-container font-semibold">
                    <th className="w-[40px] truncate max-w-[40px] pl-4 pr-2">Type</th>
                    <th className="w-[200px] truncate max-w-[200px] pr-2">Description</th>
                    <th className="w-[120px] truncate max-w-[120px] pr-2">User</th>
                    <th className="w-[100px] truncate max-w-[100px] pr-2">Customer</th>
                    <th className="w-[150px] truncate max-w-[150px] pr-2">Email</th>
                    <th className="w-[80px] truncate max-w-[80px] pr-2">Role</th>
                    <th className="w-[100px] truncate max-w-[100px] pr-4">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities.map((activity) => (
                    <tr
                      key={activity.id}
                      className="h-[56px] text-font-secondary border-b border-border hover:text-primary hover:bg-background hover:button-container cursor-pointer transition-colors ease-out-in duration-300"
                    >
                      <td className="w-[40px] truncate max-w-[40px] pl-4 pr-2">
                        <Icon
                          icon={getActivityIcon(activity.type)}
                          className={`text-2xl ${getActivityColor(activity.type)}`}
                        />
                      </td>
                      <td className="w-[200px] truncate max-w-[200px] pr-2 text-font-primary">
                        {activity.description}
                      </td>
                      <td className="w-[120px] truncate max-w-[120px] pr-2">
                        {formatUserName(activity.user)}
                      </td>
                      <td className="w-[100px] truncate max-w-[100px] pr-2">
                        {activity.customerName || 'N/A'}
                      </td>
                      <td className="w-[150px] truncate max-w-[150px] pr-2">
                        <div className="flex items-center gap-1">
                          <span className="truncate">{activity.email || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="w-[80px] truncate max-w-[80px] pr-2">
                        {activity.role || 'N/A'}
                      </td>
                      <td className="w-[100px] truncate max-w-[100px] pr-4">
                        {formatTimestamp(activity.timestamp)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View (below xl) */}
              <div className="xl:hidden space-y-2 p-2">
                {filteredActivities.map((activity) => (
                  <div
                    key={activity.id}
                    className="p-3 rounded-lg cursor-pointer transition-all duration-300 ease-out-in hover:shadow-lg hover:scale-[1.02] bg-bgSecondary card-container border border-bgAccent"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Icon
                          icon={getActivityIcon(activity.type)}
                          className={`text-2xl ${getActivityColor(activity.type)} flex-shrink-0`}
                        />
                        <div className="flex flex-col">
                          <span className="font-semibold text-primary text-sm">
                            {activity.description}
                          </span>
                          <span className="text-xs text-font-secondary">
                            {formatUserName(activity.user)}
                          </span>
                        </div>
                      </div>
                      <span className="text-xs text-font-secondary">
                        {formatTimestamp(activity.timestamp)}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-font-secondary">
                        {activity.customerName || 'N/A'}
                      </span>
                      {activity.role && (
                        <span className="text-xs text-font-secondary">
                          {activity.role}
                        </span>
                      )}
                    </div>

                    {activity.email && (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-font-secondary truncate">
                          {activity.email}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminActivityTab;